import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProcessingJourney from '../ProcessingView/ProcessingJourney';
import { getKernel, triggerIntakeAgent } from '../../utils/api';
import { POLLING_CONFIG } from '../../config';
import './KernelProcess.css';

/**
 * KernelProcess - Standalone page for the processing journey
 * Route: /kernelprocess/:kernelId
 */
const KernelProcess = () => {
  const { kernelId } = useParams();
  const navigate = useNavigate();
  const [kernel, setKernel] = useState(null);
  const [error, setError] = useState(null);
  const [hasTriggered, setHasTriggered] = useState(false);

  // Trigger intake agent and start polling
  useEffect(() => {
    if (!kernelId || hasTriggered) return;

    const startProcessing = async () => {
      try {
        // First fetch the kernel to check its status
        const response = await getKernel(kernelId);
        const currentKernel = response.kernel;
        setKernel(currentKernel);

        // Only trigger intake if kernel is in 'submitted' state
        if (currentKernel.status === 'submitted') {
          try {
            await triggerIntakeAgent(kernelId);
          } catch (triggerErr) {
            // Ignore "already processing" errors - just continue polling
            console.log('Trigger skipped (may already be processing):', triggerErr.message);
          }
        }

        setHasTriggered(true);
      } catch (err) {
        console.error('Error starting processing:', err);
        setError(err.message);
      }
    };

    startProcessing();
  }, [kernelId, hasTriggered]);

  // Poll for updates
  useEffect(() => {
    if (!hasTriggered || !kernelId) return;

    const terminalStatuses = ['public_complete', 'projection_complete', 'error'];

    const poll = async () => {
      try {
        const response = await getKernel(kernelId);
        setKernel(response.kernel);

        // Continue polling if not in terminal state
        if (!terminalStatuses.includes(response.kernel.status)) {
          return true; // Continue polling
        }
        return false; // Stop polling
      } catch (err) {
        console.error('Polling error:', err);
        return false; // Stop polling on error
      }
    };

    let attempts = 0;
    const maxAttempts = POLLING_CONFIG.maxAttempts;

    const pollInterval = setInterval(async () => {
      attempts++;
      const shouldContinue = await poll();

      if (!shouldContinue || attempts >= maxAttempts) {
        clearInterval(pollInterval);
      }
    }, POLLING_CONFIG.interval);

    // Initial poll
    poll();

    return () => clearInterval(pollInterval);
  }, [hasTriggered, kernelId]);

  const handleViewResults = () => {
    navigate(`/kernels/${kernelId}`);
  };

  if (error) {
    return (
      <div className="kernel-process">
        <div className="kernel-process__error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="kernel-process__back-btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="kernel-process">
      <header className="kernel-process__header">
        <div className="kernel-process__header-content">
          <img src="/logo.png" alt="Arbituer" className="kernel-process__logo" />
          <div className="kernel-process__header-text">
            <h1 className="kernel-process__title">Arbituer</h1>
            <p className="kernel-process__subtitle">Market Intelligence</p>
          </div>
        </div>
      </header>

      <main className="kernel-process__main">
        <ProcessingJourney
          kernelId={kernelId}
          kernel={kernel}
          onViewResults={handleViewResults}
        />
      </main>
    </div>
  );
};

export default KernelProcess;
